import { CITY_FILTER_CONFIG } from '../constants/config';

/**
 * Generic function to determine if a location name is likely a real city/municipality.
 * This function filters out neighborhoods, districts, subdivisions, and other non-city entities
 * that APIs like CountryStateCity often include in their city lists.
 * 
 * This approach is scalable and works for any country without needing hardcoded lists.
 * 
 * @param cityName - The city name to validate
 * @param countryCode - Optional country code for country-specific filtering
 * @param stateCode - Optional state code for state-specific filtering
 * @returns true if the name appears to be a valid city/municipality
 */
export const isValidCityName = (cityName: string, countryCode?: string, stateCode?: string): boolean => {
  if (!cityName || typeof cityName !== 'string') return false;
  
  // Apply generic filtering for all locations (including Puerto Rico)
  const name = cityName.trim();
  const lowerName = name.toLowerCase();
  
  // Check length constraints
  if (name.length < CITY_FILTER_CONFIG.minLength || name.length > CITY_FILTER_CONFIG.maxLength) {
    return false;
  }
  
  // Check for excluded keywords (neighborhoods, districts, etc.)
  const hasExcludedKeyword = CITY_FILTER_CONFIG.excludeKeywords.some(keyword => 
    lowerName.includes(keyword.toLowerCase())
  );
  if (hasExcludedKeyword) return false;
  
  // Check for excluded patterns (numbers, special chars, etc.)
  const matchesExcludedPattern = CITY_FILTER_CONFIG.excludePatterns.some(pattern => 
    pattern.test(name)
  );
  if (matchesExcludedPattern) return false;
  
  // Additional heuristics
  // Too many special characters might indicate a non-city
  const specialCharCount = (name.match(/[^a-zA-ZÀ-ÿ\s'-]/g) || []).length;
  if (specialCharCount > 2) return false;
  
  // Too many words might indicate a compound non-city location
  const wordCount = name.split(/\s+/).length;
  if (wordCount > 4) return false;
  
  return true;
};

/**
 * Helper function to format location for display
 */
export const formatLocation = (city?: string, state?: string, country?: string): string => {
  const parts = [city, state, country].filter(Boolean);
  return parts.join(', ');
};

/**
 * Validate if a location selection is complete
 */
export const isLocationComplete = (country?: string, state?: string, city?: string): boolean => {
  return Boolean(country && state && city);
};

/**
 * Future enhancement: Country-specific filtering rules could be added here
 * For example: getCountrySpecificFilter(countryCode, stateCode)
 * This would allow for country-specific overrides while maintaining the generic approach
 */
